import {
  activePayoutMethod,
  DEFAULT_SETTINGS,
  type DiscardPayment,
  type FunModeSettings,
  type HkosSettings,
  type HkosToggles,
  type ScoringMode,
  type SeriousModeSettings,
} from '../../rules/hkos';
import { renderBrandHeader } from '../branding';
import { esc } from '../helpers';
import { cachedLeaderboardEnabled } from '../leaderboardFlag';

export interface HouseRulesOverrides {
  toggles: HkosToggles;
  serious: SeriousModeSettings;
  fun: FunModeSettings;
}

export interface SettingsActions {
  save(overrides: HouseRulesOverrides): void;
  cancel(): void;
  /** This is the venue's leaderboard period rules: no Hand helper switch there (it is never on in a leaderboard game). */
  leaderboardRules?: boolean;
}

export const HAND_HELPER_LABEL = 'Hand helper — suggestions';

/** The switch's description. The remark about leaderboard games is only said while the leaderboard is switched on at all. */
export function handHelperDescription(leaderboardOn: boolean): string {
  return `Shows suggestions of the patterns your tiles are closest to while you play. Suggestions can be wrong.${leaderboardOn ? ' Not available in leaderboard games.' : ''}`;
}

const MAX_FAN_CAP_CEILING = 30;

export const TOGGLE_FIELDS: { key: keyof HkosToggles; label: string; description: string }[] = [
  {
    key: 'seatWindPung',
    label: 'Seat wind pung',
    description: 'Bonus fan for a pung or kong of your own seat wind tile.',
  },
  {
    key: 'prevailingWindPung',
    label: 'Prevailing wind pung',
    description: "Bonus fan for a pung or kong of the round's prevailing wind tile.",
  },
  {
    key: 'seatFlowerBonus',
    label: 'Seat flower bonus (flower number matches your seat)',
    description: 'Extra fan when a flower/season number matches your own seat number.',
  },
  {
    key: 'allFlowers',
    label: 'All Flowers (holding all 4 flower tiles)',
    description: 'Bonus fan for holding all 4 flower tiles. Replaces the seat flower bonus for the matching one.',
  },
  {
    key: 'allSeasons',
    label: 'All Seasons (holding all 4 season tiles)',
    description: 'Bonus fan for holding all 4 season tiles. Replaces the seat flower bonus for the matching one.',
  },
  {
    key: 'noFlowerBonus',
    label: 'No Flowers bonus (holding zero flowers/seasons)',
    description: 'Bonus fan for winning with zero flower or season tiles in hand.',
  },
  {
    key: 'robbingKong',
    label: 'Robbing a kong',
    description: "Bonus fan for winning on the tile another player just used to upgrade a pung into a kong.",
  },
  {
    key: 'kongReplacement',
    label: 'Kong replacement win',
    description: 'Bonus fan for self-drawing the win off the replacement tile right after declaring a kong.',
  },
  {
    key: 'lastWallTile',
    label: 'Last-wall-tile win',
    description: 'Bonus fan for winning on the very last tile drawable from the wall.',
  },
  {
    key: 'lastDiscard',
    label: 'Last-discard win',
    description: 'Bonus fan for winning by claiming the very last discard of the game.',
  },
  {
    key: 'heavenlyHand',
    label: 'Heavenly Hand',
    description: "Forced limit hand: the dealer wins with their own untouched starting hand.",
  },
  {
    key: 'earthlyHand',
    label: 'Earthly Hand',
    description: "Forced limit hand: a non-dealer wins by claiming the dealer's very first discard.",
  },
  {
    key: 'winDoubleKong',
    label: 'Win Double Kong',
    description: 'Bonus fan for self-drawing the win off a replacement tile whose kong was itself made from an earlier kong\'s replacement tile.',
  },
];

export function defaultHouseRules(): HouseRulesOverrides {
  return {
    toggles: { ...DEFAULT_SETTINGS.toggles },
    serious: { ...DEFAULT_SETTINGS.serious },
    fun: { ...DEFAULT_SETTINGS.fun },
  };
}

/**
 * minimumFan above the active mode's own fan cap would make every hand
 * unwinnable. 0 is allowed — Fun mode defaults to 0 so it doesn't restrict
 * "chicken hand" (no-fan) wins; Serious mode defaults to 1.
 */
export function validateMinimumFan(
  raw: string,
  maxAllowed: number,
): { value: number } | { error: string } {
  const n = Number(raw);
  if (raw.trim() === '' || !Number.isInteger(n)) {
    return { error: 'Minimum fan must be a whole number.' };
  }
  if (n < 0 || n > maxAllowed) {
    return { error: `Minimum fan must be between 0 and ${maxAllowed}.` };
  }
  return { value: n };
}

export function validatePayoutBase(raw: string): { value: number } | { error: string } {
  const n = Number(raw);
  if (raw.trim() === '' || !Number.isFinite(n)) {
    return { error: 'Payout base must be a number.' };
  }
  if (n < 1.5 || n > 2) {
    return { error: 'Payout base must be between 1.5 and 2.' };
  }
  return { value: n };
}

export function validateSeriousMultiplier(raw: string): { value: number } | { error: string } {
  const n = Number(raw);
  if (raw.trim() === '' || !Number.isFinite(n)) {
    return { error: 'Serious mode multiplier must be a number.' };
  }
  if (n <= 0) {
    return { error: 'Serious mode multiplier must be greater than 0.' };
  }
  return { value: n };
}

export function validateMaxFanCap(raw: string): { value: number } | { error: string } {
  const n = Number(raw);
  if (raw.trim() === '' || !Number.isInteger(n)) {
    return { error: 'Max fan cap must be a whole number.' };
  }
  if (n < 1 || n > MAX_FAN_CAP_CEILING) {
    return { error: `Max fan cap must be between 1 and ${MAX_FAN_CAP_CEILING}.` };
  }
  return { value: n };
}

export function validateFunMultiplier(raw: string): { value: number } | { error: string } {
  const n = Number(raw);
  if (raw.trim() === '' || !Number.isFinite(n)) {
    return { error: 'Fun mode multiplier must be a number.' };
  }
  if (n <= 0) {
    return { error: 'Fun mode multiplier must be greater than 0.' };
  }
  return { value: n };
}

export function renderSettings(
  root: HTMLElement,
  mode: ScoringMode,
  current: HouseRulesOverrides,
  error: string | undefined,
  actions: SettingsActions,
): void {
  const seriousSection =
    mode === 'serious'
      ? `
      <h2>Serious mode</h2>
      <label for="minimumFan">Minimum fan to win</label>
      <input type="number" id="minimumFan" min="0" step="1" value="${current.serious.minimumFan}" />

      <label for="payoutMethod">Fan → points curve</label>
      <select id="payoutMethod">
        <option value="exponential" ${(current.serious.payoutMethod ?? 'exponential') === 'exponential' ? 'selected' : ''}>Exponential (payout base) — traditional, grows fast</option>
        <option value="multiplier" ${current.serious.payoutMethod === 'multiplier' ? 'selected' : ''}>Multiplier (linear, like Fun mode) — simpler, grows steadily</option>
      </select>
      <p class="muted" style="margin-top:-0.25rem">Substitutional — pick one; only the one you pick affects scoring.</p>

      <div id="payoutBaseRow">
        <label for="payoutBase">Payout base (1.5 – 2)</label>
        <input type="number" id="payoutBase" min="1.5" max="2" step="0.05" value="${current.serious.payoutBase}" />
      </div>

      <div id="seriousMultiplierRow">
        <label for="seriousMultiplier">Multiplier (1 = raw fan)</label>
        <input type="number" id="seriousMultiplier" min="0" step="0.1" value="${current.serious.multiplier ?? 1}" />
      </div>

      <label for="seriousMaxFanCap">Max fan cap</label>
      <input type="number" id="seriousMaxFanCap" min="1" max="${MAX_FAN_CAP_CEILING}" step="1" value="${current.serious.maxFanCap}" />

      <label for="discardPayment">Discard payment</label>
      <select id="discardPayment">
        <option value="discarderPaysAll" ${current.serious.discardPayment === 'discarderPaysAll' ? 'selected' : ''}>Discarder pays all</option>
        <option value="discarderPaysDouble" ${current.serious.discardPayment === 'discarderPaysDouble' ? 'selected' : ''}>Discarder pays double</option>
      </select>

      <div class="stack">
        <label class="checkbox-row">
          <input type="checkbox" id="dealerDoublePayout" ${current.serious.dealerDoublePayout ? 'checked' : ''} />
          Dealer double-points payout
        </label>
        <label class="checkbox-row">
          <input type="checkbox" id="tenpaiPaymentOnDraw" ${current.serious.tenpaiPaymentOnDraw ? 'checked' : ''} />
          Tenpai payment on a draw
        </label>
      </div>

      <label for="tenpaiPaymentUnits">Tenpai payment units</label>
      <input type="number" id="tenpaiPaymentUnits" min="0" step="1" value="${current.serious.tenpaiPaymentUnits}" />

      <label for="baseUnit">Base unit (table stake multiplier)</label>
      <input type="number" id="baseUnit" min="0" step="1" value="${current.serious.baseUnit}" />
    `
      : '';

  const funSection =
    mode === 'fun'
      ? `
      <h2>Fun mode</h2>
      <label for="minimumFan">Minimum fan to win</label>
      <input type="number" id="minimumFan" min="0" step="1" value="${current.fun.minimumFan}" />

      <label for="funMultiplier">Multiplier (1 = raw fan)</label>
      <input type="number" id="funMultiplier" min="0" step="0.1" value="${current.fun.multiplier}" />

      <label for="funMaxFanCap">Max fan cap</label>
      <input type="number" id="funMaxFanCap" min="1" max="${MAX_FAN_CAP_CEILING}" step="1" value="${current.fun.maxFanCap}" />
    `
      : '';

  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>House Rules</h1>
      <p class="muted">
        These apply to the next game you start. A game already in progress
        keeps the settings it was started with.
      </p>

      ${error ? `<div class="error-box">${esc(error)}</div>` : ''}

      <label>Toggles</label>
      <div class="row" style="margin-bottom:0.5rem">
        <button id="pickAllBtn" class="ghost" type="button">House's Pick</button>
        <button id="clearAllBtn" class="ghost" type="button">Clear All Rules</button>
      </div>
      <div class="stack">
        ${TOGGLE_FIELDS.map(
          (f) => `
          <label class="checkbox-row rule-row">
            <input type="checkbox" id="toggle-${f.key}" ${current.toggles[f.key] ? 'checked' : ''} />
            <span>
              ${esc(f.label)}
              <span class="rule-description muted">${esc(f.description)}</span>
            </span>
          </label>`,
        ).join('')}
      </div>

      ${
        actions.leaderboardRules
          ? ''
          : `<label class="checkbox-row rule-row" style="margin-top:0.75rem">
        <input type="checkbox" id="handHelper" ${current.toggles.handHelper !== false ? 'checked' : ''} />
        <span>
          ${esc(HAND_HELPER_LABEL)}
          <span class="rule-description muted">${esc(handHelperDescription(cachedLeaderboardEnabled()))}</span>
        </span>
      </label>`
      }

      ${seriousSection}
      ${funSection}

      <div class="row" style="margin-top:1rem">
        <button id="resetBtn" class="ghost" type="button">Reset to defaults</button>
        <button id="cancelBtn" class="secondary" type="button">Cancel</button>
        <button id="saveBtn" type="button">Save</button>
      </div>
    </div>
  `;

  if (mode === 'serious') {
    // Substitutional, not both at once — only the row for the currently
    // selected curve is shown, so it's clear which fields actually affect
    // scoring right now.
    const methodSelect = root.querySelector<HTMLSelectElement>('#payoutMethod')!;
    const payoutBaseRow = root.querySelector<HTMLElement>('#payoutBaseRow')!;
    const multiplierRow = root.querySelector<HTMLElement>('#seriousMultiplierRow')!;
    const syncPayoutMethodRows = (): void => {
      const useMultiplier = methodSelect.value === 'multiplier';
      payoutBaseRow.style.display = useMultiplier ? 'none' : '';
      multiplierRow.style.display = useMultiplier ? '' : 'none';
    };
    syncPayoutMethodRows();
    methodSelect.addEventListener('change', syncPayoutMethodRows);
  }

  root.querySelector<HTMLButtonElement>('#resetBtn')!.addEventListener('click', () => {
    renderSettings(root, mode, defaultHouseRules(), undefined, actions);
  });

  // Act on the checkboxes in place rather than re-rendering, so an in-
  // progress edit elsewhere on the form (payout base, discard payment, ...)
  // isn't discarded — unlike Reset to defaults, these two only ever touch
  // the toggle list.
  root.querySelector<HTMLButtonElement>('#pickAllBtn')!.addEventListener('click', () => {
    for (const f of TOGGLE_FIELDS) {
      root.querySelector<HTMLInputElement>(`#toggle-${f.key}`)!.checked = true;
    }
  });

  root.querySelector<HTMLButtonElement>('#clearAllBtn')!.addEventListener('click', () => {
    for (const f of TOGGLE_FIELDS) {
      root.querySelector<HTMLInputElement>(`#toggle-${f.key}`)!.checked = false;
    }
  });

  root.querySelector<HTMLButtonElement>('#cancelBtn')!.addEventListener('click', () => {
    actions.cancel();
  });

  root.querySelector<HTMLButtonElement>('#saveBtn')!.addEventListener('click', () => {
    const toggles = TOGGLE_FIELDS.reduce<HkosToggles>(
      (acc, f) => {
        acc[f.key] = root.querySelector<HTMLInputElement>(`#toggle-${f.key}`)!.checked;
        return acc;
      },
      { ...DEFAULT_SETTINGS.toggles },
    );
    // Not one of the fan toggles above: its own box, absent when editing a leaderboard period's rules (the value is then left as it was).
    const helperBox = root.querySelector<HTMLInputElement>('#handHelper');
    toggles.handHelper = helperBox ? helperBox.checked : current.toggles.handHelper;

    const rawMinimumFan = root.querySelector<HTMLInputElement>('#minimumFan')!.value;

    // Only the active mode's section is on screen — validate that one
    // (including its own minimumFan) from its inputs, and carry the other
    // mode's settings through untouched.
    const validationErrors: string[] = [];
    let serious = current.serious;
    let fun = current.fun;

    if (mode === 'serious') {
      const payoutBaseResult = validatePayoutBase(
        root.querySelector<HTMLInputElement>('#payoutBase')!.value,
      );
      const seriousMultiplierResult = validateSeriousMultiplier(
        root.querySelector<HTMLInputElement>('#seriousMultiplier')!.value,
      );
      const seriousMaxFanCapResult = validateMaxFanCap(
        root.querySelector<HTMLInputElement>('#seriousMaxFanCap')!.value,
      );
      const seriousMaxFanCap =
        'value' in seriousMaxFanCapResult ? seriousMaxFanCapResult.value : current.serious.maxFanCap;
      const minimumFanResult = validateMinimumFan(rawMinimumFan, seriousMaxFanCap);
      serious = {
        payoutMethod: root.querySelector<HTMLSelectElement>('#payoutMethod')!.value as 'exponential' | 'multiplier',
        payoutBase: 'value' in payoutBaseResult ? payoutBaseResult.value : current.serious.payoutBase,
        multiplier:
          'value' in seriousMultiplierResult ? seriousMultiplierResult.value : (current.serious.multiplier ?? 1),
        maxFanCap: seriousMaxFanCap,
        baseUnit: Number(root.querySelector<HTMLInputElement>('#baseUnit')!.value) || 0,
        discardPayment: root.querySelector<HTMLSelectElement>('#discardPayment')!
          .value as DiscardPayment,
        dealerDoublePayout: root.querySelector<HTMLInputElement>('#dealerDoublePayout')!.checked,
        tenpaiPaymentOnDraw: root.querySelector<HTMLInputElement>('#tenpaiPaymentOnDraw')!.checked,
        tenpaiPaymentUnits:
          Number(root.querySelector<HTMLInputElement>('#tenpaiPaymentUnits')!.value) || 0,
        minimumFan:
          'value' in minimumFanResult ? minimumFanResult.value : current.serious.minimumFan,
      };
      for (const result of [payoutBaseResult, seriousMultiplierResult, seriousMaxFanCapResult, minimumFanResult]) {
        if ('error' in result) validationErrors.push(result.error);
      }
    } else {
      const funMultiplierResult = validateFunMultiplier(
        root.querySelector<HTMLInputElement>('#funMultiplier')!.value,
      );
      const funMaxFanCapResult = validateMaxFanCap(
        root.querySelector<HTMLInputElement>('#funMaxFanCap')!.value,
      );
      const funMaxFanCap =
        'value' in funMaxFanCapResult ? funMaxFanCapResult.value : current.fun.maxFanCap;
      const minimumFanResult = validateMinimumFan(rawMinimumFan, funMaxFanCap);
      fun = {
        multiplier: 'value' in funMultiplierResult ? funMultiplierResult.value : current.fun.multiplier,
        maxFanCap: funMaxFanCap,
        minimumFan: 'value' in minimumFanResult ? minimumFanResult.value : current.fun.minimumFan,
      };
      for (const result of [funMultiplierResult, funMaxFanCapResult, minimumFanResult]) {
        if ('error' in result) validationErrors.push(result.error);
      }
    }

    const draft: HouseRulesOverrides = {
      toggles,
      serious,
      fun,
    };

    if (validationErrors.length > 0) {
      renderSettings(root, mode, draft, validationErrors.join(' '), actions);
      return;
    }

    actions.save(draft);
  });
}

/**
 * Read-only echo of renderSettings' fields, for viewing the house rules a
 * game was actually started with — reachable by tapping the Fun/Serious
 * Mode badge during play. Deliberately not the editable form: settings are
 * snapshotted at game start (see CLAUDE.md rule 5), so an editable-looking
 * form here would wrongly imply a mid-game change could do something.
 */
export function renderHouseRulesSummary(settings: HkosSettings): string {
  const toggleRows = TOGGLE_FIELDS.map(
    (f) => `
      <label class="checkbox-row rule-row">
        <input type="checkbox" disabled ${settings.toggles[f.key] ? 'checked' : ''} />
        <span>
          ${esc(f.label)}
          <span class="rule-description muted">${esc(f.description)}</span>
        </span>
      </label>`,
  ).join('');

  const helperRow = `
      <label class="checkbox-row rule-row">
        <input type="checkbox" disabled ${settings.toggles.handHelper !== false ? 'checked' : ''} />
        <span>
          ${esc(HAND_HELPER_LABEL)}
          <span class="rule-description muted">${esc(handHelperDescription(cachedLeaderboardEnabled()))}</span>
        </span>
      </label>`;

  const modeSection =
    settings.mode === 'serious'
      ? `
      <h2>Serious mode</h2>
      <label>Minimum fan to win</label>
      <input type="number" value="${settings.serious.minimumFan}" disabled />

      <label>Fan → points curve</label>
      <input
        type="text"
        value="${activePayoutMethod(settings) === 'multiplier' ? 'Multiplier (linear, like Fun mode)' : 'Exponential (payout base)'}"
        disabled
      />

      <label>Payout base (1.5 – 2)${activePayoutMethod(settings) === 'multiplier' ? ' — not used this game' : ''}</label>
      <input type="number" value="${settings.serious.payoutBase}" disabled />

      <label>Multiplier (1 = raw fan)${activePayoutMethod(settings) === 'exponential' ? ' — not used this game' : ''}</label>
      <input type="number" value="${settings.serious.multiplier ?? 1}" disabled />

      <label>Max fan cap</label>
      <input type="number" value="${settings.serious.maxFanCap}" disabled />

      <label>Discard payment</label>
      <input
        type="text"
        value="${settings.serious.discardPayment === 'discarderPaysAll' ? 'Discarder pays all' : 'Discarder pays double'}"
        disabled
      />

      <div class="stack">
        <label class="checkbox-row">
          <input type="checkbox" disabled ${settings.serious.dealerDoublePayout ? 'checked' : ''} />
          Dealer double-points payout
        </label>
        <label class="checkbox-row">
          <input type="checkbox" disabled ${settings.serious.tenpaiPaymentOnDraw ? 'checked' : ''} />
          Tenpai payment on a draw
        </label>
      </div>

      <label>Tenpai payment units</label>
      <input type="number" value="${settings.serious.tenpaiPaymentUnits}" disabled />

      <label>Base unit (table stake multiplier)</label>
      <input type="number" value="${settings.serious.baseUnit}" disabled />
    `
      : `
      <h2>Fun mode</h2>
      <label>Minimum fan to win</label>
      <input type="number" value="${settings.fun.minimumFan}" disabled />

      <label>Multiplier (1 = raw fan)</label>
      <input type="number" value="${settings.fun.multiplier}" disabled />

      <label>Max fan cap</label>
      <input type="number" value="${settings.fun.maxFanCap}" disabled />
    `;

  return `
    <label>Toggles</label>
    <div class="stack">${toggleRows}${helperRow}</div>
    ${modeSection}
  `;
}
